import { FC, useEffect, useRef, useState } from "react";
import { DrawerState, IDrawer } from "Utils/types";
import { isMobile } from "Utils/Theme";
import useBackButton from "shared/hooks/useBackButton";
import ProfileImgSrc from "resources/Graphics/About/Amit-BW.png";
import useTranslation from "shared/hooks/useTranslation";
import useScreenSize from "shared/hooks/useScreenSize";
import { RTLTextAlignClassName } from "Utils/constants";
import { Paragraphs } from "./consts";
import {
    CenterParagraph,
    Content,
    HeaderBackground,
    HeaderMask,
    Paragraph,
    ProfileImage,
    ScrollWrapper,
    TextContainer,
    Wrapper,
} from "./AboutDrawer.style";

const AboutDrawer: FC<IDrawer> = ({
    state,
    openDelay,
    onClose
}) => {
    const isOpen = state === DrawerState.Open;
    const initialized = useRef<number>(0);
    const wrapperContainer = useRef<HTMLDivElement>(null)
    const mobile = isMobile();
    const [enterState, setEnterState] = useState<boolean>(false);
    const [isImageOpen, setImageOpen] = useState<boolean>(false);
    const [closingFlag, setClosingFlag] = useState<boolean>(false);
    const [textCloseState, setTextCloseState] = useState<boolean>(false);
    const [textScrollOffset, setTextScrollOffset] = useState<number>(0);
    const [profileImageTimeout, setProfileImageTimeout] = useState<NodeJS.Timeout>();
    const { translate, textDir } = useTranslation();
    const { width, height } = useScreenSize();
    const textContent = (
        <Content
            displayed={enterState}
            screenWidth={width}
            screenHeight={height}
        >
            <Paragraph
                className={RTLTextAlignClassName}
                dangerouslySetInnerHTML={{ __html: translate(Paragraphs?.[0]) }}
                dir={textDir}
            />
            <CenterParagraph
                className={RTLTextAlignClassName}
                dangerouslySetInnerHTML={{ __html: translate(Paragraphs?.[1]) }}
                dir={textDir}
            />
            <Paragraph
                className={RTLTextAlignClassName}
                dangerouslySetInnerHTML={{ __html: translate(Paragraphs?.[2]) }}
                dir={textDir}
            />
        </Content>
    );
    
    useBackButton("About", onClose, isOpen);
    useEffect(() => {
        if (mobile) {
            const container = wrapperContainer?.current;
            const onScroll = (): void => setTextScrollOffset(container?.scrollTop || 0);
            container &&  container.addEventListener('scroll', onScroll)

            return () => {
                container?.removeEventListener('scroll', onScroll);
            }
        }
    }, [wrapperContainer, mobile]);

    useEffect(() => {
        if (isOpen && openDelay && openDelay > 0) setTimeout(() => setEnterState(isOpen), openDelay * 1000);
        else setEnterState(isOpen);
    }, [isOpen]);
    
    useEffect(() => {
        if (enterState) {
            setProfileImageTimeout(
                setTimeout(() => {
                    setImageOpen(enterState)
                }, 250)
            );
        }
        else {
            clearTimeout(profileImageTimeout);
            setImageOpen(enterState);
        }
    }, [enterState]);

    useEffect(() => {
        if (initialized.current >= 2) {
            if (enterState) setTextCloseState(false);
            else {
                setClosingFlag(true);
                setTimeout(() => {
                    setClosingFlag(false);
                    setTextCloseState(true);
                }, 150);
            }
        }
        else initialized.current++;
    }, [enterState]);

    return (
        <Wrapper
            ref={wrapperContainer}
            open={enterState}
            scrollTop={textScrollOffset}
        >
            {isMobile() ? (
                <ScrollWrapper open={enterState}>
                    <HeaderBackground />
                    {textContent}
                </ScrollWrapper>
            ) : (
                <>
                    <ProfileImage
                        src={ProfileImgSrc}
                        alt={''}
                        open={isImageOpen}
                    />
                    <TextContainer open={enterState || closingFlag} closing={textCloseState}>
                        {textContent}
                    </TextContainer>
                </>
            )}
            <HeaderMask open={enterState} />
        </Wrapper>
    );
}

export default AboutDrawer;